// Angular
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
// Service
import { MapService } from '../../../../../services/map.service';
import { LayoutRenderService } from '../../../../services/layout-render.service';
import { ResourceMediaControlService } from '../../resource-media-control/service/resource-media-control.service';
import { BroadcastService, ToasterType, ObjectUtils } from '@cv-invision/core';
// Model
import { SMART_TAG_LOCATION_STEP, SmartTagLocationFlow } from '../model/smart-tag-location.model';
import { FormModelType, ResourceModel } from '../../resource-media-control/model/resource-media.model';
import { BroadcastGeneralEventMetadata, GeneralEventType } from '../../../models/component.events';
import { SmartTagModel } from '../model/smartTag.model';
import { CaptureType } from '../../panorama-tour/model/report-image.model';
import { SmartTagFormModel } from '../../resource-media-control/model/smart-tag-form.model';
import { LayoutComponentBroadcast } from '../../../../models/cv-layout-render.model';
// Other
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
	selector: 'cv-schema-render-smart-tag-location',
	templateUrl: './smart-tag-location.component.html',
	providers: [ ResourceMediaControlService ]
})
export class SmartTagLocationComponent implements OnInit, OnDestroy {

	@Input() data: any;
	@Input() layoutNode: any;
	// Point of reference of the Map
	@Input() mainPosition: google.maps.LatLngLiteral = null;
	// New Map Location for the Smart Tag
	@Input() smartTagLocation: any;
	@Input() pinSelectorMode: boolean;

	@Output() changePinSelectorMode: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() disableMap: EventEmitter<boolean> = new EventEmitter<boolean>();

	public path;
	public smartTagLocationFlow: SmartTagLocationFlow = new SmartTagLocationFlow();
	public smartTagLocationStep = SMART_TAG_LOCATION_STEP;

	public isSavingInProcess = false;

	get isEditable(): boolean {
		return this.layoutRenderService.activateEditableUX;
	}

	private atComponentDestroy = new Subject();

	constructor(
		private mapService: MapService,
		private resourceMediaControlService: ResourceMediaControlService,
		private layoutRenderService: LayoutRenderService,
		private broadcastService: BroadcastService
	) {
		//
	}

	ngOnInit() {
		this.path = this.data._Path;
		if (this.layoutNode && this.layoutNode.options) {
			this.layoutNode.options.groupKey = 'groupName';
			this.layoutNode.options.key = 'smartTags';
			this.layoutNode.options.externalProcess = true;
			this.layoutNode.options.version = 'v2';
		}
		// this.smartTagLocationFlow.resourceModel.targetPath = this.path;

		this.initComponent();

		// Listen to change on Map to reload the page, it needs to be executed after DATA_UPDATED
		this.layoutRenderService.componentBroadcast
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcastData: LayoutComponentBroadcast) => {
				if (broadcastData.eventType === GeneralEventType.DATA_UPDATED && broadcastData.jsonPath === this.data._Path) {
					this.initComponent();
				}
			});
	}

	/**
	 * Restart the Component
	 */
	public initComponent(): void {
		this.isSavingInProcess = false;
		this.disableMap.next(false);
		this.smartTagLocationFlow = new SmartTagLocationFlow();

		const smartTagTypeList = ObjectUtils.recursiveDeepCopy(this.layoutRenderService.getExtraDataOption('smartTagTypes') || []);
		const locationSmartTagsTypes = smartTagTypeList.filter((type: any) => ['LOCATION'].indexOf(type.key) > -1);
		const flattenLocation = ObjectUtils.flattenArrayByAttr(locationSmartTagsTypes, 'children', []).map(item => item.key);
		this.data.smartTags = this.data.smartTags.filter(smartTag => flattenLocation.indexOf(smartTag.tagType) > -1);

		if (!this.hasSmartTags()) {
			this.smartTagLocationFlow.step = SMART_TAG_LOCATION_STEP.EMPTY
		}
	}

	/**
	 * Evaluate if the Map has SmartTags
	 */
	public hasSmartTags(): boolean {
		return this.data && this.data.smartTags && this.data.smartTags.length > 0;
	}

	/**
	 * Enable Pin Mode
	 * @param event
	 */
	public setPinSelectorMode(event: boolean): void {
		if (event) {
			this.smartTagLocationFlow.step = SMART_TAG_LOCATION_STEP.PIN;
		} else if (this.hasSmartTags()) {
			this.smartTagLocationFlow.step = SMART_TAG_LOCATION_STEP.LIST;
		} else {
			this.smartTagLocationFlow.step = SMART_TAG_LOCATION_STEP.EMPTY;
		}
		this.changePinSelectorMode.next(event);
	}

	/**
	 * Edit Mode
	 * @param metadata
	 */
	public onEdit(metadata: any): void {
		this.smartTagLocationFlow.resourceModel = new ResourceModel();
		this.smartTagLocationFlow.resourceModel.targetPath = this.path;
		// Form Smart Tag on Edit Mode Copy the Model
		if (metadata.resourceMediaModel.captureType === CaptureType.SMARTTAG) {
			this.smartTagLocationFlow.resourceModel.smartTagForm = new SmartTagFormModel();
			if (metadata.resourceMediaModel.formModelType === FormModelType.EDIT) {
				this.smartTagLocationFlow.resourceModel.smartTagForm.smartTagData = metadata.smartTagData;
			}
		}

		this.smartTagLocationFlow.filterBy = ['LOCATION', 'RENEWALE_ENERGY', 'ENERGY'];
		this.smartTagLocationFlow.step = SMART_TAG_LOCATION_STEP.EDIT;
		this.changePinSelectorMode.next(false);
	}

	/**
	 * Switch to Create Mode and use the new Blob from the Marker
	 */
	public async createSnapshot(): Promise<void> {

		// Introduced by the Map change, this ensure it works with number and functions
		const location = {
			lat: (this.smartTagLocation.lat instanceof Function) ? this.smartTagLocation.lat() : this.smartTagLocation.lat,
			lng: (this.smartTagLocation.lng instanceof Function) ? this.smartTagLocation.lng() : this.smartTagLocation.lng,
		};

		// Create the Snapshot to use
		const markerBlob = await this.mapService.getStaticImageAsBlob({
			location: {
				lat: this.mainPosition.lat,
				lng: this.mainPosition.lng
			},
			markers: [{
				color: 'red',
				location: {
					lat: location.lat,
					lng: location.lng
				}
			}]
		}).toPromise();

		const selectedSmartTagItem: any = {
			key: 'LOCATION',
			pathTo: 'Location',
			value: 'Location'
		};

		// All data of the current Room
		this.smartTagLocationFlow.resourceModel = {
			blob: markerBlob,
			targetPath: this.path,
			metadata: {
				latitude: location.lat,
				longitude: location.lng
			},
			smartTagForm: new SmartTagFormModel()
		}

		this.smartTagLocationFlow.filterBy = [selectedSmartTagItem.key];
		this.smartTagLocationFlow.step = SMART_TAG_LOCATION_STEP.CREATE;

		this.disableMap.next(true);

	}

	/**
	 * Upload the snapshot to the Server
	 */
	public async onSave(): Promise<void> {

		let message = '';

		if (this.smartTagLocationFlow.step === SMART_TAG_LOCATION_STEP.EDIT) {
			await this.patchSmartTag();
			message = 'Information has been updated';
		} else {
			await this.createSmartTag();
			message = 'New Smart Tag Created';
		}

		this.broadcastService.broadcast({
			name: ToasterType.TOASTER,
			event: {
				type: 'success',
				title: `Smart Tag`,
				message: message
			}
		});

	}

	/**
	 * Create Smart Tag
	 */
	public async createSmartTag(): Promise<void> {
		const resourceModel: ResourceModel = this.smartTagLocationFlow.resourceModel;
		resourceModel.smartTagForm.asGroupKey = true;

		this.isSavingInProcess = true;

		const newSmartTagData: SmartTagModel = await this.resourceMediaControlService.createSmartTag(
			{ isOutsideArea: true, ...this.data, cv_Label: 'Property Location' },
			resourceModel.smartTagForm,
			resourceModel.targetPath,
			{ blob: resourceModel.blob, requestedBlob: true, metadata: resourceModel.metadata }
		);

		if (newSmartTagData !== null) {
			const broadcastMetadata = new BroadcastGeneralEventMetadata();
			broadcastMetadata.data = newSmartTagData;
			broadcastMetadata.path = 'smartTags';
			broadcastMetadata.isCreate = true;

			this.layoutRenderService.componentBroadcast.next({
				eventType: GeneralEventType.UPDATE_DATA,
				jsonPath: resourceModel.targetPath,
				metaData: broadcastMetadata
			});

			this.changePinSelectorMode.next(false);

			this.initComponent();
		}

		this.isSavingInProcess = false;
	}

	/**
	 * Patch the Smart Tag
	 */
	public async patchSmartTag(): Promise<any> {
		const resourceModel: ResourceModel = this.smartTagLocationFlow.resourceModel;
		const newSmartTagData: SmartTagModel = await this.resourceMediaControlService.patchSmartTag(resourceModel);

		if (newSmartTagData && newSmartTagData !== null) {
			const broadcastMetadata = new BroadcastGeneralEventMetadata();
			broadcastMetadata.data = resourceModel.smartTagForm.smartTagData;
			broadcastMetadata.path = resourceModel.smartTagForm.smartTagData._Path;
			broadcastMetadata.isReplace = true;

			this.layoutRenderService.componentBroadcast.next({
				eventType: GeneralEventType.UPDATE_DATA,
				jsonPath: resourceModel.targetPath,
				metaData: broadcastMetadata
			});

			this.changePinSelectorMode.next(false);

			this.initComponent();

		}
		this.isSavingInProcess = false;

		return newSmartTagData;
	}

	/**
	 * is Form Valid
	 */
	public isFormValid(): boolean {
		if (this.smartTagLocationFlow.resourceModel !== null) {
			return this.smartTagLocationFlow.resourceModel.smartTagForm !== null && (
				this.smartTagLocationFlow.resourceModel.smartTagForm.selectedSmartTagItem !== null &&
				this.smartTagLocationFlow.resourceModel.smartTagForm.smartTagData !== null
			);
		}
		return false;
	}

	/**
	 * Discard Process Create/Edit
	 */
	public onCancel(): void {
		this.isSavingInProcess = false;
		this.disableMap.next(false);

		if (this.smartTagLocationFlow.step === SMART_TAG_LOCATION_STEP.EDIT) {
			this.smartTagLocationFlow.step = SMART_TAG_LOCATION_STEP.LIST;
		} else {
			this.smartTagLocationFlow.step = SMART_TAG_LOCATION_STEP.PIN;
		}
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
